import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { keyed } from 'lit/directives/keyed.js';
import { Temporal } from "temporal-polyfill";
import './sighting-form.ts';
import './date-calendar.ts';
import { stepButtonStyles, stepIcon, type DateCalendar } from './date-calendar.ts';
import { cameraAddIcon, chevronLeftIcon, chevronRightIcon, linkStyles } from "./icons.ts";
import { consume } from "@lit/context";
import { userContext, type User } from "./identity.ts";
import { classMap } from "lit/directives/class-map.js";
import SightingForm from "./sighting-form.ts";
import { newSighting, observationToFormData } from "./sighting-form-data.ts";
import { v7 } from "uuid";
import { type Occurrence } from "./types.ts";
import { DEFAULT_REGION_SLUG, EARLIEST_OBSERVATION_DATE, observationToday, REGIONS } from "./constants.ts";
import { createRef, ref } from "lit/directives/ref.js";

/** The date the calendar has selected, spelled out for the day-stepping row. */
const SELECTED_DATE_LABEL = {weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'} as const;

// Regions live in constants.ts — they are the scope of the query now, not a
// list of places to look at, so the map and the occurrence fetch need them too.

/** The layout that puts the panel under the map, as salish-sea.ts lays the page out. */
export const COMPACT_LAYOUT = '(max-aspect-ratio: 1)';

@customElement('obs-panel')
export class ObsPanel extends LitElement {
  static styles = [stepButtonStyles, linkStyles, css`
    :host {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      overflow-x: hidden;
      overflow-y: auto;
      overscroll-behavior: contain;
      /* Reserve the scrollbar's width even when the day's list doesn't
         overflow, so stepping between full and empty days doesn't shift the
         header/calendar/region selector horizontally. */
      scrollbar-gutter: stable;
      scroll-behavior: smooth;
      touch-action: pan-y;
    }
    .full-bleed {
      align-self: stretch;
      margin-left: -0.5rem;
      margin-right: -0.5rem;
    }
    header {
      text-align: center;
    }
    h2 {
      font-size: var(--fs-h5);
      font-weight: 400;
      margin-bottom: 0;
      margin-top: 1rem;
    }
    /* Steps the day between the arrows, mirroring the calendar's month row
       above it. Subordinate to that heading — it restates the day the grid
       already rings, so it reads as a readout, not a second title. */
    .day-nav {
      align-items: center;
      display: flex;
      gap: 0.25rem;
      justify-content: center;
      margin: 0 auto;
      max-width: 21rem;
      padding-top: 0.25rem;
    }
    /* The readout doubles as the way to seek a distant date: the calendar pages
       a month at a time, and the corpus reaches back to 2012. Editing is behind
       a click so the resting state stays a readout, not a form control. */
    .day-nav .selected-date {
      background: none;
      border: 0;
      border-radius: 4px;
      color: var(--color-text-secondary);
      cursor: pointer;
      flex-grow: 1;
      font-family: inherit;
      font-size: var(--fs-body2);
      font-variant-numeric: tabular-nums;
      padding: 0.125rem 0.25rem;
      text-align: center;
      white-space: nowrap;
    }
    /* Says the readout opens the month, when it folds away: pointing down, and up once open. */
    .day-nav .selected-date .fold svg {
      fill: currentColor;
      height: 1rem;
      margin-left: 0.125rem;
      rotate: 90deg;
      vertical-align: -0.2rem;
      width: 1rem;
    }
    .day-nav .selected-date .fold.open svg {
      rotate: -90deg;
    }
    /* Under the open grid, when it folds: a quiet way to a date far from this month. */
    .type-date {
      align-self: center;
      background: none;
      border: 0;
      border-radius: 4px;
      color: var(--color-primary);
      cursor: pointer;
      font-family: inherit;
      font-size: var(--fs-body2);
      padding: 0.125rem 0.5rem;
    }
    .type-date:hover {
      background: var(--color-surface-subtle);
    }
    .type-date:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 1px;
    }
    /* The calendar's own :host rule says display: block, which beats the browser's
       rule for [hidden]; this one, outside its shadow root, beats that. */
    date-calendar[hidden] {
      display: none;
    }
    .day-nav .selected-date:hover {
      background: var(--color-surface-subtle);
      color: var(--color-text);
    }
    .day-nav .selected-date:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 1px;
    }
    /* Occupies the readout's slot so swapping between them doesn't shift the
       arrows either side. */
    .day-nav input[type=date] {
      background: white;
      border: 1px solid var(--color-border-field);
      border-radius: 4px;
      box-sizing: border-box;
      color: var(--color-text);
      flex-grow: 1;
      font-family: inherit;
      font-size: var(--fs-body2);
      font-variant-numeric: tabular-nums;
      min-width: 0;
      padding: 0.0625rem 0.25rem;
      text-align: center;
    }
    .day-nav input[type=date]:focus-visible {
      border-color: var(--color-primary);
      outline: 2px solid var(--color-primary);
      outline-offset: 1px;
    }
    /* The destinations are laid out rather than hidden behind a select: there
       are only five, and seeing them is most of the value. */
    .go-to {
      align-items: baseline;
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      justify-content: center;
      margin-top: 0.875rem;
    }
    #go-to-label {
      color: var(--color-text-muted);
      font-size: var(--fs-body2);
    }
    /* Small rounded buttons, as the region choices are. */
    .bubble {
      background: white;
      border: 1px solid var(--color-border-strong);
      border-radius: 999px;
      color: var(--color-text);
      cursor: pointer;
      font-family: inherit;
      font-size: var(--fs-body2);
      line-height: 1.4;
      padding: 0.1875rem 0.625rem;
      white-space: nowrap;
    }
    .bubble:hover:not(:disabled) {
      background: var(--color-surface-subtle);
      border-color: var(--color-text-muted);
      color: var(--color-text);
    }
    /* The selected region is the one piece of state in this row: it says what
       the map, the list and the calendar are all scoped to. Filled rather than
       merely outlined so it reads at a glance which one is active.

       These must come AFTER the hover rule. The hover selector has the same
       specificity as the selected-hover one — :not() contributes its argument's
       specificity — so on equal terms the later rule wins, and put first these
       would lose the fill exactly while the pointer is on them. */
    .bubble.selected {
      background: var(--color-primary);
      border-color: var(--color-primary);
      color: white;
    }
    .bubble.selected:hover:not(:disabled) {
      background: var(--color-primary-hover);
      border-color: var(--color-primary-hover);
      color: white;
    }
    .bubble:disabled {
      border-color: var(--color-border);
      color: var(--color-text-disabled);
      cursor: default;
    }
    .bubble:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 1px;
    }
    button[name=show] {
      align-items: center;
      align-self: flex-start;
      cursor: pointer;
      display: inline-flex;
      gap: 0.5rem;
      vertical-align: middle;
      background: var(--color-primary);
      border: 1px solid var(--color-primary);
      border-radius: 4px;
      color: white;
      fill: white;
      font-family: inherit;
      font-size: var(--fs-body2);
      padding: 0.5rem 1rem;
    }
    button[name=show]:hover {
      background: var(--color-primary-hover);
      border-color: var(--color-primary-hover);
    }
    sighting-form {
      background-color: var(--color-surface-tint);
    }
    .contribute-pitch {
      background: var(--color-primary-tint);
      border-left: 3px solid var(--color-primary);
      border-radius: 4px;
      color: var(--color-text);
      font-size: var(--fs-body1);
      line-height: 1.45;
      margin: 0;
      padding: 0.625rem 0.75rem;
    }
    .contribute-pitch a {
      color: var(--color-primary);
      font-weight: 400;
      white-space: nowrap;
    }
    .hide {
      display: none;
    }
  `];

  @property({attribute: true, reflect: true, type: Boolean})
  public showForm: boolean = false

  @property({type: String, reflect: true})
  private date!: string;

  @property({type: String, reflect: true})
  public regionSlug: string = DEFAULT_REGION_SLUG;

  @consume({context: userContext, subscribe: true})
  @state()
  private user: User | undefined;

  @property({attribute: false})
  private sightingForForm = {...newSighting(), id: v7()};

  /** Whether the form holds a sighting not yet saved, a blank one or a copy, which may suggest its accuracy (decision 077). */
  @state()
  private sightingIsNew = true;

  private formRef = createRef<SightingForm>();
  private calendarRef = createRef<DateCalendar>();
  private seekInputRef = createRef<HTMLInputElement>();

  /** Whether the date readout has been swapped for the seek input. */
  @state()
  private seekingDate = false;

  /**
   * Whether the panel sits under the map, as it does on a phone held upright: the map
   * then takes half the screen, and a month grid the rest, which put the day's sightings
   * a whole screen down (salish-dvd). There the grid folds away behind the date readout,
   * which opens it; the same media query lays the page out (salish-sea.ts).
   */
  @state()
  private compact = false;

  /** Whether the month grid is showing, when compact; otherwise it always is. */
  @state()
  private calendarOpen = false;

  #layout: MediaQueryList | undefined;
  #onLayoutChange = () => { this.compact = this.#layout?.matches ?? false; };

  connectedCallback(): void {
    super.connectedCallback();
    this.#layout = window.matchMedia?.(COMPACT_LAYOUT);
    this.#layout?.addEventListener('change', this.#onLayoutChange);
    this.#onLayoutChange();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#layout?.removeEventListener('change', this.#onLayoutChange);
  }

  /**
   * Set when the seek editor is dismissed from the keyboard — by Escape, or by
   * committing a typed date. Removing the input drops focus to the body, which
   * strands a keyboard user; blur is excluded because they moved focus on purpose.
   */
  #returnFocusToReadout = false;

  @property({attribute: false})
  lastOwnOccurrence: Occurrence | null = null

  protected render() {
    const {id, ...sighting} = this.sightingForForm;
    // The same bounds the calendar disables its cells against, so the steppers
    // and the grid agree on where the navigable range ends.
    const selected = Temporal.PlainDate.from(this.date);
    const atToday = Temporal.PlainDate.compare(selected, observationToday()) >= 0;
    const atEarliest = Temporal.PlainDate.compare(selected, EARLIEST_OBSERVATION_DATE) <= 0;
    return html`
      <header>
        <h2>Marine Mammal Observations</h2>
        <date-calendar ${ref(this.calendarRef)} date=${this.date} regionSlug=${this.regionSlug}
          ?hidden=${this.compact && !this.calendarOpen} @date-selected=${this.onCalendarDateSelected}></date-calendar>
        <div class="day-nav">
          <button class="step" @click=${this.onGotoYesterday} type="button" name="yesterday" ?disabled=${atEarliest} aria-label="Previous day">${stepIcon(chevronLeftIcon)}</button>
          ${this.seekingDate ? html`
            <input
              ${ref(this.seekInputRef)}
              type="date"
              .value=${this.date}
              min=${EARLIEST_OBSERVATION_DATE.toString()}
              max=${observationToday().toString()}
              aria-label="Jump to a date"
              @change=${this.onSeekDate}
              @keydown=${this.onSeekKeydown}
              @blur=${this.stopSeekingDate}
            >
          ` : html`
            <button
              class="selected-date"
              type="button"
              title=${this.compact ? (this.calendarOpen ? 'Hide the month' : 'Show the month') : 'Jump to a date'}
              aria-expanded=${this.compact ? String(this.calendarOpen) : nothing}
              @click=${this.compact ? this.toggleCalendar : this.startSeekingDate}
            >${Temporal.PlainDate.from(this.date).toLocaleString(undefined, SELECTED_DATE_LABEL)}${this.compact
              ? html`<span class=${classMap({fold: true, open: this.calendarOpen})}>${stepIcon(chevronRightIcon)}</span>` : nothing}</button>
          `}
          <button class="step" @click=${this.onGotoTomorrow} type="button" name="tomorrow" ?disabled=${atToday} aria-label="Next day">${stepIcon(chevronRightIcon)}</button>
        </div>
        ${this.compact && this.calendarOpen && !this.seekingDate ? html`
          <button class="type-date" type="button" @click=${this.startSeekingDate}>Type a date</button>
        ` : nothing}
        <div class="go-to" role="group" aria-labelledby="go-to-label">
          <span id="go-to-label">Showing:</span>
          ${REGIONS.map(({slug, label}) => html`
            <button
              class=${classMap({bubble: true, selected: slug === this.regionSlug})}
              type="button"
              aria-pressed=${slug === this.regionSlug ? 'true' : 'false'}
              @click=${() => this.selectRegion(slug)}
            >${label}</button>
          `)}
          <button
            class="bubble"
            type="button"
            ?disabled=${!this.lastOwnOccurrence}
            @click=${this.goToLastOwnOccurrence}
          >My last observation</button>
        </div>
      </header>
      ${keyed(id, html`
        <sighting-form
          ${ref(this.formRef)}
          class=${classMap({"full-bleed": true, hide: !this.showForm})}
          @cancel-edit=${this.onCancelEdit}
          @sighting-saved=${this.onSightingSaved}
          .initialValues=${sighting}
          .isNew=${this.sightingIsNew}
          sightingId=${id}
          date=${this.date}
        ></sighting-form>
      `)}
      ${!this.user && !this.showForm ? html`
        <p class="contribute-pitch">
          The public is our <strong>best source of data on how marine mammals use these waters</strong> — add what you see.
          <a href="/about.html">Learn more.</a>
        </p>
      ` : ''}
      <button class=${classMap({hide: this.showForm})} @click=${this.doShowForm} type="button" name="show">
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px">${cameraAddIcon}</svg>
        <span>Add a Sighting</span>
      </button>
      <slot></slot>
    `;
  }

  /** Open the form on a saved sighting, or with `copy`, on a new one copied from it. */
  async editObservation(observation: Occurrence, {copy = false} = {}) {
    await this.doShowForm();
    // Prefer America/Los_Angeles for consistency with sighting-form validation
    this.sightingForForm = observationToFormData(observation);
    this.sightingIsNew = copy;
    this.formRef.value!.scrollIntoView();
  }

  private onCancelEdit() {
    this.showForm = false;
    this.sightingForForm = {...newSighting(), id: v7()};
    this.sightingIsNew = true;
  }

  /** A new read-path build landed: bring the calendar's circles up to date. */
  revalidateCalendar(): void {
    this.calendarRef.value?.revalidate();
  }

  private onSightingSaved() {
    this.showForm = false;
    this.sightingForForm = {...newSighting(), id: v7()};
    this.sightingIsNew = true;
    // The calendar's circle for the new sighting grows when the short build its
    // save starts is published (revalidateCalendar); until then the month's file
    // doesn't count it, so there is nothing to refetch.
  }

  // Both steppers guard as well as disable. The upper bound moves while the page
  // is open (a tab left running past local midnight), and a guard here is the
  // only thing standing between a stray step and a date the calendar won't
  // render — it disables every cell outside the range, so the selection lands
  // somewhere the grid can't show and the occurrences query returns nothing for.
  private onGotoYesterday() {
    const date = Temporal.PlainDate.from(this.date).subtract({days: 1});
    if (Temporal.PlainDate.compare(date, EARLIEST_OBSERVATION_DATE) < 0)
      return;
    this.dispatchEvent(new CustomEvent('date-selected', {bubbles: true, composed: true, detail: date.toString()}));
  }

  private onGotoTomorrow() {
    const date = Temporal.PlainDate.from(this.date).add({days: 1});
    if (Temporal.PlainDate.compare(date, observationToday()) > 0)
      return;
    this.dispatchEvent(new CustomEvent('date-selected', {bubbles: true, composed: true, detail: date.toString()}));
  }

  private toggleCalendar() {
    this.calendarOpen = !this.calendarOpen;
  }

  /**
   * A day picked from the grid; folded away again when compact, as the day is chosen.
   * The day's button goes with it, so a keyboard user's focus goes back to the readout
   * that opened the grid, as it does from the typed date (updated()).
   */
  private onCalendarDateSelected() {
    if (this.compact && this.calendarOpen)
      this.#returnFocusToReadout = true;
    this.calendarOpen = false;
  }

  private startSeekingDate() {
    this.seekingDate = true;
    // Under the map, the typed date takes the grid's place rather than sitting under it.
    this.calendarOpen = false;
    // Focus once the input exists, so typing can start straight away. The native
    // picker is left to its own indicator rather than opened from here: it would
    // cover our calendar with a second one, and typing a distant date is the
    // affordance a month-at-a-time grid is missing.
    this.updateComplete.then(() => this.seekInputRef.value?.focus());
  }

  private stopSeekingDate() {
    this.seekingDate = false;
  }

  private onSeekDate(e: Event) {
    const {value} = e.target as HTMLInputElement;
    this.seekingDate = false;
    this.#returnFocusToReadout = true;
    // `min`/`max` only style an out-of-range value, they don't stop one being
    // entered — and a cleared or half-typed field fires change on some browsers.
    // Same bounds the steppers and the grid enforce, so none of them can disagree.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
      return;
    const date = Temporal.PlainDate.from(value);
    if (Temporal.PlainDate.compare(date, EARLIEST_OBSERVATION_DATE) < 0
      || Temporal.PlainDate.compare(date, observationToday()) > 0)
      return;
    this.dispatchEvent(new CustomEvent('date-selected', {bubbles: true, composed: true, detail: value}));
  }

  private onSeekKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      // Don't let it bubble to anything that treats Escape as a broader dismiss.
      e.stopPropagation();
      this.#returnFocusToReadout = true;
      this.stopSeekingDate();
    }
  }

  protected updated(): void {
    // The readout only exists once the input has been rendered away, so this
    // has to wait for the update rather than run in the handler.
    if (this.#returnFocusToReadout && !this.seekingDate) {
      this.#returnFocusToReadout = false;
      this.renderRoot.querySelector<HTMLButtonElement>('.selected-date')?.focus();
    }
  }

  /**
   * Selecting a region changes what the map, this list and the calendar are
   * scoped to — it is not just a camera move. salish-sea.ts owns that state and
   * also does the zoom, so the slug is all that travels.
   */
  private selectRegion(slug: string) {
    this.dispatchEvent(new CustomEvent('region-selected', {bubbles: true, composed: true, detail: slug}));
  }

  private goToLastOwnOccurrence() {
    if (!this.lastOwnOccurrence)
      return;
    this.dispatchEvent(new CustomEvent('focus-occurrence', {bubbles: true, composed: true, detail: this.lastOwnOccurrence}));
  }

  private async doShowForm() {
    if (!this.user) {
      this.dispatchEvent(new Event('log-in', {bubbles: true, composed: true}));
      return;
    }

    this.showForm = true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "obs-panel": ObsPanel;
  }
}
